import * as React from 'react';
import type {
  DataKey,
  DefaultLegendContentProps,
  DefaultTooltipContentProps,
  LegendPayload,
  ResponsiveContainerProps,
  TooltipContentProps,
  TooltipPayloadEntry,
  TooltipValueType,
} from 'recharts';
import * as RechartsPrimitive from 'recharts';

import { cn } from 'src/lib/utils';

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: '', dark: '.dark' } as const;
const INITIAL_CHART_DIMENSION = { width: 1, height: 1 } as const;

type ChartContainerRenderProps = {
  tooltipPortal: HTMLElement | null;
};

type ChartContainerProps = Omit<React.ComponentProps<'div'>, 'children'> &
  Pick<
    ResponsiveContainerProps,
    | 'aspect'
    | 'debounce'
    | 'height'
    | 'initialDimension'
    | 'maxHeight'
    | 'minHeight'
    | 'minWidth'
    | 'onResize'
    | 'width'
  > & {
    config: ChartConfig;
    children:
      | React.ReactNode
      | ((props: ChartContainerRenderProps) => React.ReactNode);
    responsiveContainerProps?: Omit<ResponsiveContainerProps, 'children'>;
    useAspectRatio?: boolean;
  };

type ChartTooltipContentProps = Partial<
  TooltipContentProps<TooltipValueType, string | number>
> & {
  className?: string;
  color?: string;
  hideIndicator?: boolean;
  hideLabel?: boolean;
  indicator?: 'line' | 'dot' | 'dashed';
  labelClassName?: DefaultTooltipContentProps['labelClassName'];
  labelKey?: string;
  nameKey?: string;
};

type ChartLegendContentProps = React.ComponentProps<'div'> &
  Pick<DefaultLegendContentProps, 'payload' | 'verticalAlign'> & {
    hideIcon?: boolean;
    nameKey?: string;
  };

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string,
) {
  if (typeof payload !== 'object' || payload === null) {
    return;
  }

  const payloadPayload =
    'payload' in payload &&
    typeof payload.payload === 'object' &&
    payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === 'string'
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === 'string'
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string;
  }

  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config];
}

function stringifyDataKey(dataKey: DataKey<unknown> | undefined) {
  return typeof dataKey === 'function' ? undefined : dataKey;
}

function getTooltipIndicatorColor(
  item: TooltipPayloadEntry,
  fallbackColor?: string,
) {
  if (fallbackColor) {
    return fallbackColor;
  }

  const itemPayload = item.payload;
  if (
    typeof itemPayload === 'object' &&
    itemPayload !== null &&
    'fill' in itemPayload &&
    typeof itemPayload.fill === 'string'
  ) {
    return itemPayload.fill;
  }

  return item.color;
}

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  );
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }

  return context;
}

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config]) => config.theme || config.color,
  );

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Chart logic
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
      itemConfig.color;
    return color ? `  --color-${key}: ${color};` : null;
  })
  .join('\n')}
}
`,
          )
          .join('\n'),
      }}
    />
  );
};

const ChartContainer = ({
  id,
  aspect,
  className,
  children,
  config,
  debounce,
  height,
  initialDimension,
  maxHeight,
  minHeight,
  minWidth,
  onResize,
  responsiveContainerProps,
  useAspectRatio = false,
  width,
  ref,
  ...props
}: ChartContainerProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;
  const [tooltipPortal, setTooltipPortal] =
    React.useState<HTMLDivElement | null>(null);

  const setContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const chartChildren =
    typeof children === 'function' ? children({ tooltipPortal }) : children;

  return (
    <ChartContext value={{ config }}>
      <div
        className={cn(
          "relative flex justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-[var(--chart-text)] [&_.recharts-cartesian-grid_line]:stroke-[var(--chart-grid)] [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-[var(--chart-grid)] [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-[var(--chart-reference-line)] [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-hidden [&_.recharts-surface]:outline-hidden",
          'min-h-0 min-w-0',
          useAspectRatio && 'aspect-video',
          className,
        )}
        data-chart={chartId}
        ref={setContainerRef}
        {...props}
      >
        <ChartStyle config={config} id={chartId} />
        <RechartsPrimitive.ResponsiveContainer
          aspect={aspect}
          debounce={debounce}
          height={height}
          initialDimension={initialDimension ?? INITIAL_CHART_DIMENSION}
          maxHeight={maxHeight}
          minHeight={minHeight}
          minWidth={minWidth ?? 0}
          onResize={onResize}
          width={width}
          {...responsiveContainerProps}
        >
          {chartChildren}
        </RechartsPrimitive.ResponsiveContainer>
        <div
          aria-hidden
          className='pointer-events-none absolute inset-0'
          ref={setTooltipPortal}
        />
      </div>
    </ChartContext>
  );
};
ChartContainer.displayName = 'Chart';

const ChartTooltip = RechartsPrimitive.Tooltip;

const ChartTooltipContent = ({
  active,
  payload,
  className,
  indicator = 'dot',
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey,
  ref,
}: ChartTooltipContentProps & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const { config } = useChart();

  const tooltipLabel = React.useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null;
    }

    const [item] = payload;
    const dataKey = stringifyDataKey(item?.dataKey);
    const key = `${labelKey || dataKey || item?.name || 'value'}`;
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value =
      !labelKey && typeof label === 'string'
        ? config[label as keyof typeof config]?.label || label
        : itemConfig?.label;

    if (labelFormatter) {
      return (
        <div className={cn('font-medium', labelClassName)}>
          {labelFormatter(value, payload)}
        </div>
      );
    }

    if (!value) {
      return null;
    }

    return <div className={cn('font-medium', labelClassName)}>{value}</div>;
  }, [
    label,
    labelFormatter,
    payload,
    hideLabel,
    labelClassName,
    config,
    labelKey,
  ]);

  if (!(active && payload?.length)) {
    return null;
  }

  const nestLabel = payload.length === 1 && indicator !== 'dot';

  return (
    <div
      className={cn(
        'grid min-w-[12rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl',
        'border-[var(--chart-tooltip-border)] bg-[var(--chart-tooltip-bg)]',
        className,
      )}
      ref={ref}
    >
      {!nestLabel ? tooltipLabel : null}
      <div className='mt-1 grid gap-2'>
        {payload.map((item, index) => {
          const dataKey = stringifyDataKey(item.dataKey);
          const key = `${nameKey || item.name || dataKey || 'value'}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          const indicatorColor = getTooltipIndicatorColor(item, color);

          return (
            <div
              className={cn(
                'flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground',
                indicator === 'dot' && 'items-center',
              )}
              key={`${dataKey || item.name || index}`}
            >
              {formatter && item?.value !== undefined && item.name != null ? (
                formatter(item.value, item.name, item, index, item.payload)
              ) : (
                <>
                  {itemConfig?.icon ? (
                    <itemConfig.icon />
                  ) : (
                    !hideIndicator && (
                      <div
                        className={cn(
                          'shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)',
                          {
                            'h-2.5 w-2.5': indicator === 'dot',
                            'w-1': indicator === 'line',
                            'w-0 border-[1.5px] border-dashed bg-transparent':
                              indicator === 'dashed',
                            'my-0.5': nestLabel && indicator === 'dashed',
                          },
                        )}
                        style={
                          {
                            '--color-bg': indicatorColor,
                            '--color-border': indicatorColor,
                          } as React.CSSProperties
                        }
                      />
                    )
                  )}
                  <div
                    className={cn(
                      'flex flex-1 justify-between leading-none',
                      nestLabel ? 'items-end' : 'items-center',
                    )}
                  >
                    <div className='mr-4 grid gap-1.5'>
                      {nestLabel ? tooltipLabel : null}
                      <span className='text-muted-foreground'>
                        {itemConfig?.label || item.name}
                      </span>
                    </div>
                    {item.value != null && (
                      <span className='font-medium font-mono text-foreground tabular-nums'>
                        {item.value.toLocaleString()}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
ChartTooltipContent.displayName = 'ChartTooltip';

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = ({
  className,
  hideIcon = false,
  payload,
  verticalAlign = 'bottom',
  nameKey,
  ref,
}: ChartLegendContentProps & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const { config } = useChart();

  if (!payload?.length) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-4',
        verticalAlign === 'top' ? 'pb-3' : 'pt-3',
        className,
      )}
      ref={ref}
    >
      {payload.map((item: LegendPayload) => {
        const dataKey = stringifyDataKey(item.dataKey);
        const key = `${nameKey || dataKey || 'value'}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);

        return (
          <div
            className={cn(
              'flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground',
            )}
            key={item.value}
          >
            {itemConfig?.icon && !hideIcon ? (
              <itemConfig.icon />
            ) : (
              <div
                className='h-2 w-2 shrink-0 rounded-[2px]'
                style={{
                  backgroundColor: item.color,
                }}
              />
            )}
            {itemConfig?.label}
          </div>
        );
      })}
    </div>
  );
};
ChartLegendContent.displayName = 'ChartLegend';

export {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
};
